import { addDays, format, startOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { supabase } from "@/lib/supabase";
import { weekStartOf } from "@/lib/api/polls";

export interface WeeklyAvailability {
  id: string;
  group_id: string;
  user_id: string;
  player_id: number | null;
  available_date: string;
  week_start: string;
}

export interface DayAvailabilitySummary {
  date: string;
  label: string;
  count: number;
}

export function getWeekDays(weekStart: string = weekStartOf()): Date[] {
  const start = new Date(`${weekStart}T12:00:00`);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export const DAY_WINDOW_SIZE = 7;
/** Days ahead to prefetch availability for the sliding day picker. */
export const AVAILABILITY_LOOKAHEAD_DAYS = 28;
export const DAY_WINDOW_MAX_OFFSET = AVAILABILITY_LOOKAHEAD_DAYS - DAY_WINDOW_SIZE;

/** Next `count` calendar days starting from today (inclusive). */
export function getUpcomingDays(count = 7, from: Date = new Date()): Date[] {
  return getDayWindow(count, 0, from);
}

/** Sliding window of `windowSize` days starting `offset` days after today. */
export function getDayWindow(
  windowSize = DAY_WINDOW_SIZE,
  offset = 0,
  from: Date = new Date()
): Date[] {
  const today = format(from, "yyyy-MM-dd");
  const start = addDays(new Date(`${today}T12:00:00`), offset);
  return Array.from({ length: windowSize }, (_, i) => addDays(start, i));
}

function weekStartsForDays(days: Date[]): string[] {
  return [...new Set(days.map((d) => weekStartOf(d)))];
}

export async function fetchWeeklyAvailabilityForDays(
  groupId: string,
  days: Date[]
): Promise<WeeklyAvailability[]> {
  const weekStarts = weekStartsForDays(days);
  const results = await Promise.all(
    weekStarts.map((ws) => fetchWeeklyAvailability(groupId, ws))
  );
  return results.flat();
}

export async function fetchUserAvailabilityDatesForDays(
  groupId: string,
  userId: string,
  days: Date[]
): Promise<string[]> {
  const weekStarts = weekStartsForDays(days);
  const results = await Promise.all(
    weekStarts.map((ws) => fetchUserAvailabilityDates(groupId, userId, ws))
  );
  return [...new Set(results.flat())];
}

export function formatDayLabel(date: Date): string {
  return format(date, "EEE d", { locale: es });
}

export async function fetchWeeklyAvailability(
  groupId: string,
  weekStart: string = weekStartOf()
): Promise<WeeklyAvailability[]> {
  const { data, error } = await supabase
    .from("weekly_availability")
    .select("*")
    .eq("group_id", groupId)
    .eq("week_start", weekStart);

  if (error) throw error;
  return (data ?? []) as WeeklyAvailability[];
}

export async function fetchUserAvailabilityDates(
  groupId: string,
  userId: string,
  weekStart: string = weekStartOf()
): Promise<string[]> {
  const { data, error } = await supabase
    .from("weekly_availability")
    .select("available_date")
    .eq("group_id", groupId)
    .eq("user_id", userId)
    .eq("week_start", weekStart);

  if (error) throw error;
  return (data ?? []).map((r) => r.available_date as string);
}

export function aggregateAvailabilityByDay(
  rows: WeeklyAvailability[],
  weekDays: Date[]
): DayAvailabilitySummary[] {
  const countByDate = new Map<string, number>();
  for (const row of rows) {
    countByDate.set(row.available_date, (countByDate.get(row.available_date) ?? 0) + 1);
  }

  return weekDays.map((day) => {
    const date = format(day, "yyyy-MM-dd");
    return {
      date,
      label: formatDayLabel(day),
      count: countByDate.get(date) ?? 0,
    };
  });
}

export async function toggleWeeklyAvailability(
  groupId: string,
  availableDate: string,
  weekStart: string,
  userId: string,
  playerId?: number | null
): Promise<boolean> {
  const { data: existing, error: fetchError } = await supabase
    .from("weekly_availability")
    .select("id")
    .eq("group_id", groupId)
    .eq("user_id", userId)
    .eq("available_date", availableDate)
    .maybeSingle();

  if (fetchError) throw fetchError;

  if (existing) {
    const { error } = await supabase
      .from("weekly_availability")
      .delete()
      .eq("id", existing.id);
    if (error) throw error;
    return false;
  }

  const { error } = await supabase.from("weekly_availability").insert({
    group_id: groupId,
    user_id: userId,
    player_id: playerId ?? null,
    available_date: availableDate,
    week_start: weekStart,
  });
  if (error) throw error;
  return true;
}
